import { RefObject, useCallback, useEffect, useState } from "react";

import { flushSync } from "react-dom";
import { useTimeout } from "usehooks-ts";

import { usePageVisibility } from "src/authoringMultiplayerLock/usePageVisibility";
import { UPDATE_DEBOUNCE_WAIT_TIME } from "src/store/GraphStore";

type Args = {
  refList?: RefObject<HTMLElement>[];
  considerMarkerClass?: string;
};

/**
 * Returns all headlessui ids of the elements up the tree from the fromElement to the toElement.
 * @param fromElement - The element to start the search from.
 * @param toElement - The element to stop the search at.
 * @returns An array of headlessui ids.
 */
const getUpTreeHeadlessUiIds = (fromElement: Element, toElement: Element) => {
  if (!toElement.contains(fromElement)) return [];

  const ids = [];
  let currentElement: Element | null = fromElement;

  while (
    currentElement &&
    currentElement !== toElement &&
    currentElement !== document.body
  ) {
    if (currentElement.id.startsWith("headlessui-")) {
      ids.push(currentElement.id);
    }
    currentElement = currentElement.parentElement;
  }

  return ids;
};

/**
 * Resolves the actual focused element when dealing with portal-rendered content.
 *
 * In cases where the active element is inside a HeadlessUI portal, this function
 * attempts to find the corresponding trigger element (e.g., a button) that opened
 * the portal. This allows for correct focus tracking even when interacting with
 * portal content.
 *
 * @param activeElement - The currently active DOM element
 * @returns The referenced trigger element if found within a portal, otherwise returns the original active element
 */
export const getReferencedElement = (activeElement: Element | null) => {
  try {
    const portal = document.getElementById("headlessui-portal-root");

    if (portal && activeElement && portal.contains(activeElement)) {
      // Get the headlessui ids of the floating element
      const ids = getUpTreeHeadlessUiIds(activeElement, portal);

      if (ids.length > 0) {
        const root = document.getElementById("root");
        // If there is an element that controls this portaled element, return it
        const referencedElement = root?.querySelector(
          ids.map((id) => `[aria-controls="${id}"]`).join(","),
        );

        // If the referenced element is found, return it
        if (referencedElement) {
          return referencedElement;
        }
      }
    }

    // If the referenced element is not found, return the active element
    return activeElement;
  } catch {
    return activeElement;
  }
};

/**
 * Hook to be used when acquiring resource locks on focus.
 * Returned boolean is true when a child of a passed ref is focused or a child of an element with the passed marker class is focused.
 * Bluring out of the child refs gets reflected with a delay to avoid flickering the value when switching focus between inputs.
 * Optionally only considers childs of elements that are marked with the lock-on-focus class.
 */
export const useLockableChildFocused = ({
  refList,
  considerMarkerClass,
}: Args) => {
  const [childHasLockableFocus, direct_setHasFocus] = useState(false);
  const [delayFocusLossOnBlur, setDelayFocusLossOnBlur] = useState<
    number | null
  >(null);
  useTimeout(() => {
    direct_setHasFocus(false);
  }, delayFocusLossOnBlur);

  const setChildHasLockableFocus = useCallback(
    (updatedChildHasLockableFocus: boolean) => {
      // Flush synchronously so the re-render happens between this focus event
      // and the subsequent click. Otherwise downstream consumers (e.g.
      // useSubmitForm.disabled) read a stale value during the click handler.
      flushSync(() => {
        setDelayFocusLossOnBlur(null);
        direct_setHasFocus(updatedChildHasLockableFocus);
      });
    },
    [],
  );

  const setDelayedFocusLost = useCallback(() => {
    setDelayFocusLossOnBlur(UPDATE_DEBOUNCE_WAIT_TIME * 2);
  }, []);

  useEffect(() => {
    const handleFocusChange = () => {
      const referencedElement = getReferencedElement(document.activeElement);
      const isLockableChildFocused =
        (!refList ||
          refList.some((ref) => ref.current?.contains(referencedElement))) &&
        (!considerMarkerClass ||
          referencedElement?.closest(`.${considerMarkerClass}`) !== null);
      if (isLockableChildFocused) {
        setChildHasLockableFocus(isLockableChildFocused);
      } else {
        // delaying focus loss event to avoid giving lock away when switching focus between inputs within the
        // lockable focus container element
        setDelayedFocusLost();
      }
    };
    const handleBlur = () => {
      setDelayedFocusLost();
    };
    window.addEventListener("focus", handleFocusChange, true);
    window.addEventListener("blur", handleBlur, true);
    return () => {
      window.removeEventListener("focus", handleFocusChange, true);
      window.removeEventListener("blur", handleBlur, true);
    };
  }, [
    refList,
    considerMarkerClass,
    setChildHasLockableFocus,
    setDelayedFocusLost,
  ]);

  // When the document is no longer active (e.g. when switching browser tabs) we blur out of the lockable element
  // so that when the user returns they don't automtically reaquire the lock without first polling for
  // updates done in the mean time.
  const pageVisible = usePageVisibility();
  useEffect(() => {
    if (
      !pageVisible &&
      childHasLockableFocus &&
      document.activeElement instanceof HTMLElement
    ) {
      document.activeElement.blur();
    }
  }, [childHasLockableFocus, pageVisible]);

  return childHasLockableFocus;
};
